import { catalogOpsClient, gatewayRequest } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import type { ProductLeafPage } from '~/features/deliveries/types/delivery-runs.types';
import type { ApiResponse } from '~/features/onboarding/types/connection.types';

import type {
  BatchFunnel,
  FunnelCurationProductsResponse,
  FunnelCurationRejectedResponse,
  FunnelProductPage,
  FunnelProductsQuery,
  FunnelProductTrace,
} from '../types/batch-funnel.api.types';
import type { StageErrorsResponse } from '../types/stage-errors.api.types';

/** GET jobs/{id}/funnel — the whole batch funnel in one read. Param is the job
 *  uuid (or experiment id). Wire shape is the domain shape, so no mapper. */
export async function getBatchFunnel(jobId: string): Promise<BatchFunnel> {
  return catalogOpsClient.get(endpoints.catalogOperations.batchFunnel(jobId)).json<BatchFunnel>();
}

/** GET jobs/{id}/funnel/products — the products behind a funnel number. Nullish
 *  query values are dropped so server defaults (`ENRICHED` / `FAILED`) apply. */
export async function getFunnelProducts(
  jobId: string,
  query: FunnelProductsQuery,
): Promise<FunnelProductPage> {
  const searchParams: Record<string, string | number> = {};
  if (query.stage) searchParams.stage = query.stage;
  if (query.outcome) searchParams.outcome = query.outcome;
  if (query.reason) searchParams.reason = query.reason;
  if (query.page !== undefined) searchParams.page = query.page;
  if (query.size !== undefined) searchParams.size = query.size;
  return catalogOpsClient
    .get(endpoints.catalogOperations.batchFunnelProducts(jobId), { searchParams })
    .json<FunnelProductPage>();
}

/** GET jobs/{id}/errors — every recorded stage error for the batch (all run
 *  attempts). An empty list means nothing was recorded, not that all is well. */
export async function getBatchStageErrors(jobId: string): Promise<StageErrorsResponse> {
  return catalogOpsClient
    .get(endpoints.catalogOperations.batchStageErrors(jobId))
    .json<StageErrorsResponse>();
}

/** GET jobs/{id}/funnel/products/{productId}/trace — one product's journey. */
export async function getFunnelProductTrace(
  jobId: string,
  productId: string,
): Promise<FunnelProductTrace> {
  return catalogOpsClient
    .get(endpoints.catalogOperations.batchFunnelProductTrace(jobId, productId))
    .json<FunnelProductTrace>();
}

/** Curated products of a run via the funnel's `productsPath`
 *  (`/curation/runs/{id}/products`). Curation pages are 1-based. */
export async function getFunnelCurationProducts(
  productsPath: string,
  page: number,
  size: number,
): Promise<FunnelCurationProductsResponse> {
  return gatewayRequest(productsPath, { searchParams: { page, size } }).json();
}

/** Rejected / excluded rows via `productsPath` (`/curation/runs/{id}/rejected-items?…`).
 *  A ≤1,000-row sample per reason; `counts` carries the full totals. */
export async function getFunnelCurationRejected(
  productsPath: string,
  page: number,
  size: number,
): Promise<FunnelCurationRejectedResponse> {
  return gatewayRequest(productsPath, { searchParams: { page, size } }).json();
}

/** Per-product delivery results via `productsPath`
 *  (`/api/v1/integration/deliveries/runs/{id}/products?…`). The path already
 *  carries the outcome / errorCode filter; only paging is added. */
export async function getFunnelDeliveryProducts(
  productsPath: string,
  limit: number,
  offset: number,
): Promise<ProductLeafPage> {
  const res = await gatewayRequest(productsPath, { searchParams: { limit, offset } }).json<
    ApiResponse<ProductLeafPage>
  >();
  return res.data;
}

/** POST `actions.resetPath` — resets a running catalog-operations / feed-audit
 *  batch. Fire-and-forget: 202 means the signal was accepted, not that it stopped. */
export async function resetFunnelBatch(resetPath: string): Promise<void> {
  await gatewayRequest(resetPath, { method: 'post' });
}
